import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import LockResetIcon from "@mui/icons-material/LockResetOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { getUsers, setUserPassword } from "../../api/adminService";
import { formatDate } from "./constants";

const BUSINESS_OWNER_TYPE = 2;
const DRIVER_TYPE = 3;
const DELIVERY_COMPANY_TYPE = 5;

const TYPE_META = {
  [BUSINESS_OWNER_TYPE]: { label: "Business Owner", color: "success", icon: BusinessCenterOutlinedIcon },
  [DRIVER_TYPE]: { label: "Delivery Driver", color: "secondary", icon: DeliveryDiningOutlinedIcon },
  [DELIVERY_COMPANY_TYPE]: { label: "Delivery Company", color: "warning", icon: DomainOutlinedIcon },
};

const TABS = [
  { value: BUSINESS_OWNER_TYPE, label: "Business Owners" },
  { value: DRIVER_TYPE, label: "Drivers" },
  { value: DELIVERY_COMPANY_TYPE, label: "Delivery Companies" },
];

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export default function AccountAccess() {
  const [tab, setTab] = useState(BUSINESS_OWNER_TYPE);
  const [passwordTarget, setPasswordTarget] = useState(null);

  return (
    <Box>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Account Access
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set a new password for business owners, delivery companies and drivers. Existing
          passwords are stored as one-way hashes and can never be displayed - only replaced.
        </Typography>
      </Stack>

      <Paper
        variant="outlined"
        sx={{ mb: 3, p: 1, borderRadius: 3, display: "inline-flex", maxWidth: "100%", overflow: "hidden" }}
      >
        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          slotProps={{ indicator: { sx: { display: "none" } } }}
          sx={{ minHeight: 40 }}
        >
          {TABS.map((t) => {
            const Icon = TYPE_META[t.value].icon;
            return (
              <Tab
                key={t.value}
                value={t.value}
                icon={<Icon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={t.label}
                sx={{
                  minHeight: 40,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: "text.secondary",
                  px: 2,
                  mr: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "#fff",
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Paper>

      <AccountTable key={tab} userType={tab} onSetPassword={setPasswordTarget} />

      {passwordTarget && (
        <SetPasswordDialog
          user={passwordTarget}
          onClose={() => setPasswordTarget(null)}
          onSave={async (password) => {
            await setUserPassword(passwordTarget.userId, password);
          }}
        />
      )}
    </Box>
  );
}

function AccountTable({ userType, onSetPassword }) {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  function loadUsers() {
    setLoading(true);
    getUsers({
      type: userType,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load accounts. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadUsers();
  }

  const columns = useMemo(
    () => [
      {
        field: "fullName",
        headerName: "Account",
        flex: 1.4,
        minWidth: 240,
        renderCell: (params) => {
          const meta = TYPE_META[userType];
          return (
            <Stack direction="row" spacing={1.5} sx={{ py: 1, alignItems: "center" }}>
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: `${meta.color}.main`,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {getInitials(params.row.fullName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {params.row.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                  {params.row.email}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      ...(userType === BUSINESS_OWNER_TYPE
        ? [{ field: "businessName", headerName: "Business", flex: 1, minWidth: 180, valueGetter: (v) => v || "-" }]
        : []),
      ...(userType === DELIVERY_COMPANY_TYPE
        ? [{ field: "companyName", headerName: "Company", flex: 1, minWidth: 180, valueGetter: (v) => v || "-" }]
        : []),
      ...(userType === DRIVER_TYPE
        ? [
            {
              field: "deliveryCompanyName",
              headerName: "Delivery Company",
              flex: 1,
              minWidth: 180,
              valueGetter: (v) => v || "Unlinked",
            },
          ]
        : []),
      {
        field: "createdAt",
        headerName: "Joined",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
      {
        field: "actions",
        headerName: "",
        width: 170,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockResetIcon fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              onSetPassword(params.row);
            }}
            sx={{ borderRadius: 2.5 }}
          >
            Set Password
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userType]
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        variant="outlined"
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ p: 2, mb: 2, borderRadius: 3 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", sm: "center" } }}
        >
          <TextField
            placeholder="Search name, email or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 260, flexGrow: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button type="submit" variant="outlined" sx={{ borderRadius: 2.5 }}>
            Search
          </Button>
          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {rowCount.toLocaleString()} {rowCount === 1 ? "account" : "accounts"}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rowHeight={64}
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}

function SetPasswordDialog({ user, onClose, onSave }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const isValid = password.length >= 8 && password === confirmPassword;

  async function handleSubmit() {
    setSaving(true);
    setError("");
    try {
      await onSave(password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data || "Could not update the password.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Set New Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.fullName} &middot; {user.email}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {error && (
            <Alert severity="error">{typeof error === "string" ? error : "Something went wrong."}</Alert>
          )}
          {success && (
            <Alert severity="success">
              Password updated. Share it with {user.fullName} through a secure channel - it will not be
              shown again.
            </Alert>
          )}

          {!success && (
            <>
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="At least 8 characters"
                fullWidth
                autoFocus
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={Boolean(confirmPassword) && confirmPassword !== password}
                helperText={
                  Boolean(confirmPassword) && confirmPassword !== password ? "Passwords do not match" : " "
                }
                fullWidth
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {success ? (
          <Button variant="contained" disableElevation onClick={onClose} sx={{ borderRadius: 2.5, px: 3 }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!isValid || saving}
              onClick={handleSubmit}
              sx={{ borderRadius: 2.5, px: 3 }}
            >
              Save Password
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
